import React, { useEffect, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Spinner,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import Header from "./Header";
import Footer from "./Footer";
import { MAIN, MAIN_BACKGROUND } from "./colours";
import { fetchSessionStatus, formatGBP } from "./booking/api";

function Row({ label, value }) {
  return (
    <Flex justify="space-between" py="2" borderBottom={`1px solid ${MAIN}`} opacity={0.95}>
      <Text opacity={0.75}>{label}</Text>
      <Text fontWeight="bold" textAlign="right">
        {value}
      </Text>
    </Flex>
  );
}

export default function BookingConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let active = true;
    if (!sessionId) {
      setState({ loading: false, error: "No booking reference found.", data: null });
      return;
    }
    fetchSessionStatus(sessionId)
      .then((data) => {
        if (active) setState({ loading: false, error: "", data });
      })
      .catch((err) => {
        if (active)
          setState({
            loading: false,
            error: err.message || "We couldn't load your booking.",
            data: null,
          });
      });
    return () => {
      active = false;
    };
  }, [sessionId]);

  const { loading, error, data } = state;

  return (
    <>
      <Header />
      <Flex bgColor={MAIN_BACKGROUND} justify="center" py={{ base: "16", md: "24" }} minH="60vh">
        <Box px={{ base: "6", md: "10" }} maxW="720px" w="100%" color={MAIN}>
          {loading && (
            <Flex direction="column" align="center" py="20">
              <Spinner size="xl" color={MAIN} thickness="3px" />
              <Text mt="6">Confirming your booking…</Text>
            </Flex>
          )}

          {!loading && error && (
            <Box>
              <Heading size="xl" mb="4">
                Something went wrong
              </Heading>
              <Text mb="6">{error}</Text>
              <Text mb="8" fontSize="sm" opacity={0.8}>
                If you were charged, don't worry — please email{" "}
                <a href="mailto:happyfigfoods@gmail.com" style={{ textDecoration: "underline" }}>
                  happyfigfoods@gmail.com
                </a>{" "}
                with your name and we'll confirm your booking straight away.
              </Text>
              <Button as={RouterLink} to="/" bg={MAIN} color={MAIN_BACKGROUND} borderRadius="none" _hover={{ bg: "transparent", color: MAIN, border: `2px solid ${MAIN}` }}>
                Back home
              </Button>
            </Box>
          )}

          {!loading && !error && data && !data.paid && (
            <Box>
              <Heading size="xl" mb="4">
                Payment not completed
              </Heading>
              <Text mb="8">
                It looks like your payment wasn't completed, so no booking has been made and you
                haven't been charged. You're welcome to try again.
              </Text>
              <Button as={RouterLink} to="/walled-garden-supper-club" bg={MAIN} color={MAIN_BACKGROUND} borderRadius="none" _hover={{ bg: "transparent", color: MAIN, border: `2px solid ${MAIN}` }}>
                Back to booking
              </Button>
            </Box>
          )}

          {!loading && !error && data && data.paid && data.booking && (
            <Box>
              <Heading size="2xl" mb="3">
                You're booked in!
              </Heading>
              <Text fontSize="lg" mb="8">
                Thank you, {data.booking.lead.firstName}. This page is your booking confirmation —
                we'd suggest taking a screenshot for your records. A payment receipt will be sent to{" "}
                <strong>{data.booking.lead.email}</strong>. There's no printed ticket needed.
              </Text>

              <Box border={`2px solid ${MAIN}`} p="5" textAlign="center" mb="10">
                <Text opacity={0.75} fontSize="sm">
                  Booking reference
                </Text>
                <Heading size="xl" letterSpacing="2px" mt="1">
                  {data.booking.ref}
                </Heading>
              </Box>

              <Heading size="md" mb="3">
                Booking details
              </Heading>
              <Box mb="10">
                {data.event && <Row label="Event" value={data.event.name} />}
                {data.event && <Row label="Date" value={data.event.dateLabel} />}
                <Row label="Sitting" value={data.booking.sittingLabel} />
                <Row label="Guests" value={String(data.booking.guestCount)} />
                <Row label="Amount paid" value={formatGBP(data.amountTotal)} />
                {data.event && <Row label="Venue" value={data.event.venueName} />}
              </Box>

              {data.event && data.event.arrival && (
                <Box mb="10">
                  <Heading size="md" mb="3">
                    Arrival
                  </Heading>
                  <Text>{data.event.arrival}</Text>
                </Box>
              )}

              <Box mb="10">
                <Heading size="md" mb="4">
                  Your guests
                </Heading>
                <SimpleGrid columns={1} spacing="3">
                  {data.booking.guests.map((g, i) => (
                    <Box key={i} border={`1px solid ${MAIN}`} p="4">
                      <Text fontWeight="bold">
                        {i + 1}. {g.name}
                      </Text>
                      {(g.dietary || g.allergies || g.notes) && (
                        <Divider borderColor={MAIN} opacity={0.4} my="2" />
                      )}
                      {g.dietary && <Text fontSize="sm">Dietary: {g.dietary}</Text>}
                      {g.allergies && <Text fontSize="sm">Allergies: {g.allergies}</Text>}
                      {g.notes && <Text fontSize="sm">Notes: {g.notes}</Text>}
                    </Box>
                  ))}
                </SimpleGrid>
                {data.booking.seatingRequests && (
                  <Text mt="4" fontSize="sm">
                    <strong>Seating requests:</strong> {data.booking.seatingRequests}
                  </Text>
                )}
              </Box>

              <Button as={RouterLink} to="/" bg={MAIN} color={MAIN_BACKGROUND} borderRadius="none" _hover={{ bg: "transparent", color: MAIN, border: `2px solid ${MAIN}` }}>
                Back home
              </Button>
            </Box>
          )}
        </Box>
      </Flex>
      <Footer />
    </>
  );
}
